import { WorkflowStepDataPoll } from '@calm/evexia-web-sdk';
import { PlayArrow } from '@mui/icons-material';
import { POLL_ID } from '@rhg/constants';
import { usePoll } from '@rhg/hooks/use-poll';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import { Button, ButtonProps } from '@rhg/libs/material';
import { ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { NextButtonOnClickHandler, NextButtonParameter } from './types';

export type ProgramNextButtonProps = Omit<ButtonProps, 'onClick'> & {
    onClick: NextButtonOnClickHandler;
};
export function ProgramNextButton({
    onClick,
    ...props
}: ProgramNextButtonProps) {
    const {
        currentSession,
        orderedSessions,
        currentWorkflowStep,
        programState,
        firstSession,
    } = useProvidedProgramContext();
    const { formatMessage } = useIntl();

    const handledItem = useMemo<NextButtonParameter>(() => {
        if (programState !== ProgramState.InProgress) {
            return { type: 'guide', guide: orderedSessions[0] };
        } else if (currentWorkflowStep?.stepDefn.type === 'poll') {
            return { type: 'workflowStep', step: currentWorkflowStep };
        } else {
            return {
                type: 'guide',
                guide: currentSession || firstSession,
            };
        }
    }, [
        currentSession,
        currentWorkflowStep,
        firstSession,
        orderedSessions,
        programState,
    ]);

    const { poll, isFetched: isPollStepFetched } = usePoll(
        handledItem?.step?.stepDefn.type === 'poll'
            ? (handledItem?.step?.stepDefn.data as WorkflowStepDataPoll)
                  .relatedId
            : undefined
    );

    const isTrailer = useMemo(
        () => handledItem.guide?.isTrailer,
        [handledItem.guide?.isTrailer]
    );

    const title = useMemo(() => {
        if (isTrailer) {
            return formatMessage(commonMessages['common.play_trailer']);
        } else if (handledItem?.step?.stepDefn.type === 'poll') {
            return isPollStepFetched
                ? poll?.id === POLL_ID.MENTAL_HEALTH
                    ? formatMessage(commonMessages['common.retake_survey'], {
                          title: poll?.title,
                      })
                    : formatMessage(commonMessages['common.take_survey'], {
                          title: poll?.title,
                      })
                : formatMessage(commonMessages['common.loading']);
        } else {
            return formatMessage(commonMessages['common.play_session_number'], {
                number: handledItem?.guide?.position,
            });
        }
    }, [
        handledItem?.guide?.position,
        handledItem?.step?.stepDefn.type,
        isPollStepFetched,
        isTrailer,
        poll?.id,
        poll?.title,
        formatMessage,
    ]);

    const isDisabled = useMemo(
        () => handledItem?.step?.stepDefn.type === 'poll' && !isPollStepFetched,
        [handledItem?.step?.stepDefn.type, isPollStepFetched]
    );

    const handleClick = useCallback(() => {
        onClick(handledItem);
    }, [handledItem, onClick]);

    return (
        <Button
            variant={isTrailer ? 'outlined' : 'contained'}
            startIcon={handledItem.type === 'guide' ? <PlayArrow /> : undefined}
            onClick={handleClick}
            disabled={isDisabled}
            size="large"
            {...props}
        >
            {title}
        </Button>
    );
}
